import type { Platform, PlatformScope } from './types';
import { platformsInScope } from './types';
import { platformResult, type PlatformResult } from './results';

/** Per-platform metrics, money already normalized to display units (see money.ts). */
export interface PlatformMetrics {
  spend: number;
  results: number; // conversions (Google) | leads (Meta)
  clicks: number;
  impressions: number;
}

export type MetricsByPlatform = Partial<Record<Platform, PlatformMetrics>>;

export interface ScopeSummary {
  /** Summable across platforms — same unit, safe to add. */
  spend: number;
  clicks: number;
  impressions: number;
  /** Results stay per-platform and are NEVER summed (conversions ≠ leads). */
  results: PlatformResult[];
  /** In-scope platforms that actually have metrics. */
  platforms: Platform[];
}

/**
 * Aggregate the KPI tiles for a scope: spend / clicks / impressions are summed
 * across the in-scope platforms (same unit), while results are kept as a
 * per-platform list so the UI shows "612 conversions · 289 leads" and never a
 * merged figure — the "no false-equivalence" rule of the consolidated surface.
 */
export function summariseScope(metrics: MetricsByPlatform, scope: PlatformScope): ScopeSummary {
  const platforms = platformsInScope(scope).filter((p) => metrics[p]);
  let spend = 0;
  let clicks = 0;
  let impressions = 0;
  const results: PlatformResult[] = [];
  for (const p of platforms) {
    const m = metrics[p]!;
    spend += m.spend;
    clicks += m.clicks;
    impressions += m.impressions;
    results.push(platformResult(p, m.results));
  }
  return { spend, clicks, impressions, results, platforms };
}
